export type ExpiryTone = "ok" | "soon" | "expired";

export interface ExpiryLabel {
  /** Full, self-contained phrase — render as-is, do not prefix. */
  text: string;
  tone: ExpiryTone;
}

/** Tokens minted with the "forever" option land ~1000y out; treat anything past this as non-expiring. */
const FOREVER_THRESHOLD_MS = 365 * 50 * 86_400_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/**
 * Describe when a share token expires, relative to `now`.
 *
 * Durations are floored rather than rounded: a token with 47 hours left has
 * "1 day" of usable life, not 2. Sub-day windows are reported in hours so a
 * link dying this afternoon never reads as "in 0 days".
 */
export function expiryLabel(expiresAt: string, now: number = Date.now()): ExpiryLabel {
  const exp = new Date(expiresAt).getTime();
  if (Number.isNaN(exp)) return { text: "Expiry unknown", tone: "expired" };

  const remaining = exp - now;
  if (remaining <= 0) return { text: "Expired", tone: "expired" };
  if (remaining >= FOREVER_THRESHOLD_MS) return { text: "Never expires", tone: "ok" };

  if (remaining < HOUR_MS) return { text: "Expires in under an hour", tone: "soon" };
  if (remaining < DAY_MS) {
    return { text: `Expires in ${plural(Math.floor(remaining / HOUR_MS), "hour")}`, tone: "soon" };
  }

  const days = Math.floor(remaining / DAY_MS);
  return { text: `Expires in ${plural(days, "day")}`, tone: days <= 7 ? "soon" : "ok" };
}
